import React, { useState, useEffect } from 'react';
import { TextField, Typography, Box } from '@mui/material';
import { FieldMapping } from '../../services/fieldMapping.service';
import { isFieldHidden, isFieldRequired } from '../../utils/formUtils';

interface HospitalizationInformationProps {
  data?: any;
  onChange?: (data: any) => void;
  formData?: any;
  onFormDataChange?: (data: any) => void;
  fieldMappings?: FieldMapping[];
  isRequired?: boolean;
  readOnly?: boolean;
}

interface FormData {
  // Hospitalization 1
  hospitalization1_facility: string;
  hospitalization1_admission_date: string;
  hospitalization1_discharge_date: string;
  hospitalization1_reason: string;

  // Hospitalization 2
  hospitalization2_facility: string;
  hospitalization2_admission_date: string;
  hospitalization2_discharge_date: string;
  hospitalization2_reason: string;

  // Hospitalization 3
  hospitalization3_facility: string;
  hospitalization3_admission_date: string;
  hospitalization3_discharge_date: string;
  hospitalization3_reason: string;

  // Hospitalization 4
  hospitalization4_facility: string;
  hospitalization4_admission_date: string;
  hospitalization4_discharge_date: string;
  hospitalization4_reason: string;
}

const HospitalizationInformation: React.FC<HospitalizationInformationProps> = ({
  data,
  onChange,
  formData: initialFormData,
  onFormDataChange,
  fieldMappings = [],
  readOnly = false,
}) => {
  const actualFormData = initialFormData || data || {};
  const actualOnChange =
    onFormDataChange ||
    onChange ||
    ((_data: any) => { /* noop */ });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const isFieldHiddenLocal  = (f: string) => isFieldHidden(f, 'hospitalization_information', fieldMappings);
  const isFieldRequiredLocal = (f: string) => isFieldRequired(f, 'hospitalization_information', fieldMappings);
  const [formData, setFormData] = useState<FormData>({
    // Hospitalization 1
    hospitalization1_facility: '',
    hospitalization1_admission_date: '',
    hospitalization1_discharge_date: '',
    hospitalization1_reason: '',

    // Hospitalization 2
    hospitalization2_facility: '',
    hospitalization2_admission_date: '',
    hospitalization2_discharge_date: '',
    hospitalization2_reason: '',

    // Hospitalization 3
    hospitalization3_facility: '',
    hospitalization3_admission_date: '',
    hospitalization3_discharge_date: '',
    hospitalization3_reason: '',

    // Hospitalization 4
    hospitalization4_facility: '',
    hospitalization4_admission_date: '',
    hospitalization4_discharge_date: '',
    hospitalization4_reason: '',
    ...actualFormData,
  });

  useEffect(() => {
    setFormData((prev: FormData) => ({ ...prev, ...actualFormData }));
  }, [actualFormData]);

  const handleChange = (field: string, value: any) => {
    if (readOnly) return; // Prevent changes in read-only mode
    const updatedData = { ...formData, [field]: value };
    setFormData(updatedData);
    actualOnChange(updatedData);
  };

  const renderHospitalizationFields = (hospitalizationNumber: number) => (
    <Box
      sx={{ border: 1, borderColor: 'grey.300', borderRadius: 1, p: 2, mb: 2 }}
    >
      <Typography variant='subtitle1' gutterBottom>
        Hospitalization {hospitalizationNumber}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          label='Facility Name'
          value={
            formData[
              `hospitalization${hospitalizationNumber}_facility` as keyof FormData
            ]
          }
          onChange={e =>
            handleChange(
              `hospitalization${hospitalizationNumber}_facility`,
              e.target.value
            )
          }
          required={isFieldRequiredLocal(`hospitalization${hospitalizationNumber}_facility`)}
          fullWidth
        />
        <TextField
          label='Admission Date'
          type='date'
          value={
            formData[
              `hospitalization${hospitalizationNumber}_admission_date` as keyof FormData
            ]
          }
          onChange={e =>
            handleChange(
              `hospitalization${hospitalizationNumber}_admission_date`,
              e.target.value
            )
          }
          InputLabelProps={{ shrink: true }}
          fullWidth
        />
        <TextField
          label='Discharge Date'
          type='date'
          value={
            formData[
              `hospitalization${hospitalizationNumber}_discharge_date` as keyof FormData
            ]
          }
          onChange={e =>
            handleChange(
              `hospitalization${hospitalizationNumber}_discharge_date`,
              e.target.value
            )
          }
          InputLabelProps={{ shrink: true }}
          fullWidth
        />
        <TextField
          label='Reason for Hospitalization'
          multiline
          rows={2}
          value={
            formData[
              `hospitalization${hospitalizationNumber}_reason` as keyof FormData
            ]
          }
          onChange={e =>
            handleChange(
              `hospitalization${hospitalizationNumber}_reason`,
              e.target.value
            )
          }
          fullWidth
        />
      </Box>
    </Box>
  );

  return (
    <Box>
      <Typography variant='h5' gutterBottom>
        Hospitalization Information
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {renderHospitalizationFields(1)}
        {renderHospitalizationFields(2)}
        {renderHospitalizationFields(3)}
        {renderHospitalizationFields(4)}
      </Box>
    </Box>
  );
};

export default HospitalizationInformation;
